import type { LevelDef } from './levels';
import { isLevelReady, primeLevel } from './maps';

/**
 * Builds the next round's map in the background. The host draws it at the start of a round and says so
 * in every snapshot (Snapshot.nextMap), so each client has a whole round to build it in a worker and
 * the round change finds it ready instead of freezing the game for a second or more. If the worker is
 * missing, slow or broken, the level is simply built on the spot as before.
 */

/** What the prefetcher needs of a worker: a Worker in the browser, a stand-in in tests. */
export interface LevelWorkerLike {
  postMessage(message: { id: string }): void;
  addEventListener(type: 'message', cb: (e: { data: { id?: unknown; level?: unknown } }) => void): void;
  addEventListener(type: 'error', cb: () => void): void;
  terminate?(): void;
}

function browserWorker(): LevelWorkerLike | null {
  if (typeof Worker === 'undefined') return null;
  try {
    return new Worker(new URL('./levelWorker.ts', import.meta.url), { type: 'module' }) as unknown as LevelWorkerLike;
  } catch {
    return null;
  }
}

let makeWorker: () => LevelWorkerLike | null = browserWorker;
/** undefined: not started yet; null: none (unsupported, or it failed). */
let worker: LevelWorkerLike | null | undefined;
const asked = new Set<string>();

function ensureWorker(): LevelWorkerLike | null {
  if (worker !== undefined) return worker;
  const w = makeWorker();
  worker = w;
  w?.addEventListener('message', (e) => {
    const { id, level } = e.data ?? {};
    if (typeof id === 'string' && level && typeof level === 'object') primeLevel(id, level as LevelDef);
  });
  w?.addEventListener('error', () => {
    // It could not load or it died: give up on it. Levels are built on the spot from here on.
    w.terminate?.();
    worker = null;
    asked.clear();
  });
  return worker;
}

/** Starts building a level in the background. Safe to call again and again; does nothing for a level that is quick to resolve anyway. */
export function prefetchLevel(id: string | null | undefined): void {
  if (!id || asked.has(id) || isLevelReady(id)) return;
  const w = ensureWorker();
  if (!w) return;
  asked.add(id);
  w.postMessage({ id });
}

/** Tests hand in a stand-in worker, or none (the next call starts afresh). */
export function setLevelWorkerFactory(factory: () => LevelWorkerLike | null): void {
  worker?.terminate?.();
  makeWorker = factory;
  worker = undefined;
  asked.clear();
}
